import {
    IActiveSubscriptionRedeeemGiftCardResponse,
    IGiftCardUserResubscribeReq,
    IGiftCardUserResubscribeResponseData,
    IGiftCardUserResubscribeVerifyReq,
    IGiftCardUserResubscribeVerifyResponse,
    IUserObjectAsGiftCardSubscription,
    IValidateGiftCardResponseData,
    IValidateGiftCardResponse,
} from '../../entity/interface/iDiscounts';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import { IValidateGiftCardUseCase } from '../../core/usecases/discount/ValidateGiftCardUseCase';
import { ISubscribeViaGiftCardUseCase } from '../../core/usecases/discount/SubscribeViaGiftCardUseCase';
import { IReSubscribeViaGiftCardUseCase } from '../../core/usecases/discount/ReSubscribeViaGiftCardUseCase';
import { IGiftCardUserResubscribeVerifyUseCase } from '../../core/usecases/discount/GiftCardUserResubscribeVerifyUseCase';
import { IGiftCardUserResubscribeUseCase } from '../../core/usecases/discount/GiftCardUserResubscribeUseCase';
import { IActiveSubscriptionRedeeemGiftCardUseCase } from '../../core/usecases/discount/ActiveSubscriptionRedeeemGiftCardUseCase';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';

export interface IDiscountService {
    validateGiftCard(card_number: string, event_type: string): Promise<IValidateGiftCardResponseData>;

    subscribeViaGiftCard(
        discountCode: string,
        username: string,
        lang: string,
        password: string,
        tierGuid: string
    ): Promise<IUserObjectAsGiftCardSubscription>;

    reSubscribeViaGiftCard(
        username: string,
        tierGuid: string,
        discountCode: string,
        token: string,
        identityToken: string,
        authSign: string,
        authToken: string
    ): Promise<boolean>;

    giftCardUserResubscribe(
        payload: IGiftCardUserResubscribeReq,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IGiftCardUserResubscribeResponseData>;

    giftCardUserResubscribeVerify(
        payload: IGiftCardUserResubscribeVerifyReq,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IGiftCardUserResubscribeVerifyResponse['data']>;

    activeSubscriptionRedeeemGiftCard(
        username: string,
        tierGuid: string,
        card_number: string,
        identityToken: string,
        token: string,
        authSign: string,
        authToken: string
    ): Promise<IActiveSubscriptionRedeeemGiftCardResponse>;
}
export class DiscountService implements IDiscountService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get validateGiftCardUseCase(): IValidateGiftCardUseCase {
        return container.resolve<IValidateGiftCardUseCase>(DI.USECASES.IValidateGiftCardUseCase);
    }

    private get subViaGiftCardUseCase(): ISubscribeViaGiftCardUseCase {
        return container.resolve<ISubscribeViaGiftCardUseCase>(DI.USECASES.ISubscribeViaGiftCardUseCase);
    }

    private get reSubViaGiftCardUseCase(): IReSubscribeViaGiftCardUseCase {
        return container.resolve<IReSubscribeViaGiftCardUseCase>(DI.USECASES.IReSubscribeViaGiftCardUseCase);
    }

    private get giftCardUserResubVerifyUseCase(): IGiftCardUserResubscribeVerifyUseCase {
        return container.resolve<IGiftCardUserResubscribeVerifyUseCase>(
            DI.USECASES.IGiftCardUserResubscribeVerifyUseCase
        );
    }

    private get giftCardUserResubUseCase(): IGiftCardUserResubscribeUseCase {
        return container.resolve<IGiftCardUserResubscribeUseCase>(DI.USECASES.IGiftCardUserResubscribeUseCase);
    }

    private get activeSubRedeemUseCase(): IActiveSubscriptionRedeeemGiftCardUseCase {
        return container.resolve<IActiveSubscriptionRedeeemGiftCardUseCase>(
            DI.USECASES.IActiveSubscriptionRedeeemGiftCardUseCase
        );
    }

    private get formSignatureObjectUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }
    /**
     * Validate gift card voucher
     * @param card_number
     * @param event_type
     * @returns
     */
    async validateGiftCard(card_number: string, event_type: string): Promise<IValidateGiftCardResponseData> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.validateGiftCardUseCase.execute(card_number, event_type, csrfToken);
    }

    /**
     * Subscribe user with gift card voucher
     * @param discountCode
     * @param username
     * @param lang
     * @param password
     * @param tierGuid
     * @returns
     */
    async subscribeViaGiftCard(
        discountCode: string,
        username: string,
        lang: string,
        password: string,
        tierGuid: string
    ): Promise<IUserObjectAsGiftCardSubscription> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return this.subViaGiftCardUseCase.execute(discountCode, username, lang, password, tierGuid, csrfToken);
    }

    /**
     * Resubscribe to a tier using gift code.
     * @param {string} apiKey Application APIKEY to the user.
     * @param {string} username Logged in username.
     * @param {string} tierGuid Subscribing Tier guid.
     * @param {string} discountCode Discount code.
     * @param {string} language Language selected by user.
     * @param {string} token Authenticated user token.
     * @param {string} identityToken Autenticated user identity token.
     * @returns {boolean} Return true in case of successful update.
     */

    async reSubscribeViaGiftCard(
        username: string,
        tierGuid: string,
        discountCode: string,
        token: string,
        identityToken: string,
        authSign: string,
        authToken: string
    ): Promise<boolean> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.reSubViaGiftCardUseCase.execute(
            username,
            tierGuid,
            discountCode,
            token,
            identityToken,
            authSign,
            authToken,
            csrfToken
        );
    }

    /**
     * Redeem gift card voucher
     * @param username
     * @param identityToken
     * @param discountCode
     * @returns
     */
    async activeSubscriptionRedeeemGiftCard(
        username: string,
        tierGuid: string,
        discountCode: string,
        identityToken: string,
        token: string
    ): Promise<IActiveSubscriptionRedeeemGiftCardResponse> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }

        return await this.activeSubRedeemUseCase.execute(username, identityToken, discountCode, token, csrfToken);
    }

    /**
     * Gift card user Resubscribe to a tier.
     * @param {IGiftCardUserResubscribeReq} payload Autenticated user identity token.
     * @param {string} authToken Autenticated user token.
     * @param {string} authSign Autenticated user signature.
     * @returns {IGiftCardUserResubscribeResponseData} Return response.
     */

    async giftCardUserResubscribe(
        payload: IGiftCardUserResubscribeReq,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IGiftCardUserResubscribeResponseData> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.giftCardUserResubUseCase.execute(payload, authSign, authSign, identityToken, csrfToken);
    }

    /**
     * Gift card user Resubscribe Otp Verify.
     * @param {IGiftCardUserResubscribeVerifyReq} payload Autenticated user identity token.
     * @param {string} authToken Autenticated user token.
     * @param {string} authSign Autenticated user signature.
     * @returns {IGiftCardUserResubscribeVerifyResponse[data]} Return response.
     */

    async giftCardUserResubscribeVerify(
        payload: IGiftCardUserResubscribeVerifyReq,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<IGiftCardUserResubscribeVerifyResponse['data']> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.giftCardUserResubVerifyUseCase.execute(
            payload,
            authToken,
            authSign,
            identityToken,
            csrfToken
        );
    }
}
